import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
    AppointmentLocation,
    AppointmentPhysicalStoreInformations,
    Appointments,
    IPhysicalStoreInformationsPayload,
} from '@interfaces/myAppointmentInterfaces';

import { RootState } from 'src/store/reducers';

import WrapperSummary from '@components/styled-UI/WrapperSummary';
import LoaderTable from '@loaders/LoaderTable';

import URLS from '@utils/urlList';
import { getLabelFallback, getMockedFile } from '@utils/utils';
import { mapPhysicalStoreInformations } from '@utils/pages-utils/EyeExamsUtils';
import { getStringFromDate } from '@utils/dateUtils';

import axios, { AxiosRequestConfig } from 'axios';

export interface Props {
    appointment: Appointments;
}

/**The function below retrieves the physical store informations */

export async function getPhysicalStoreInformations(
    payload: IPhysicalStoreInformationsPayload
): Promise<any> {
    const { isMockRequest, physicalStoreId, storeId } = payload;

    if (isMockRequest) {
        const physicalStoreInformations =
            '/data/mocks/my_appointments_in_store_services_get_physical_store_information_mock.json';
        return getMockedFile(physicalStoreInformations);
    } else {
        const physicalStoreInformationsUrl = URLS.GET_PHYSICAL_STORE_INFORMATIONS.replace(
            '{storeId}',
            storeId
        ).replace('{physicalStoreId}', physicalStoreId)
        .replace('{noCache}', new Date().getTime().toString() );

        const config: AxiosRequestConfig = {
            method: 'GET',
            url: physicalStoreInformationsUrl,
            headers: {
                'Content-Type': 'application/json',
            },
        };
        return axios(config);
    }
}

/** The function below redirects user to the external page */

const goToExternalPage = (
    params: Record<'url' | 'storeId' | 'appointmentId' | 'storeNumber', string>
) => {
    const { url, storeId, appointmentId, storeNumber } = params;
    window.open(
        url
            .replace('{storeId}', storeId)
            .replace('{appointmentId}', appointmentId)
            .replace('{storeNumber}', storeNumber),
        '_parent'
    );
};

/** The function below returns the full in-store services office address */

const getFullOfficeAddress = (location: AppointmentLocation): string => {
    return `${location.officeAddress}, ${location.city}, ${location.province}, ${location.postalCode}`;
};

const renderWrapperHeader = (appointment: Appointments, config: any): JSX.Element => {
    const { date, changeAllowed, appointmentId, storeNumber } = appointment;
    return (
        <div
            role="region"
            aria-label="in-store services details date label"
            key={appointmentId}
            className="wrapper-summary-header-in-store"
        >
            {date && (
                <div className="wrapper-summary-header-date">
                    {getLabelFallback('MYAPPOINTMENTS_IN_STORE_SERVICES_CREATION_DATE_LABEL')
                        .replace(
                            '{IN_STORE_DATE}',
                            getStringFromDate(new Date(date), 'EEEE, MMM dd yyyy')
                        )
                        .replace('{IN_STORE_HOUR}', getStringFromDate(new Date(date), 'HH:mm a'))}
                </div>
            )}
            {changeAllowed && (
                <div
                    role="region"
                    aria-label="in-store services details buttons"
                    className="wrapper-summary-header-in-store-buttons"
                >
                    <button
                        className="primary-button-negative button-size-small"
                        data-element-id="Account_BookAnAppointment"
                        data-description="Reschedule"
                        onClick={() =>
                            goToExternalPage({
                                url: config.callFromComponentToTO.myAppointments.inStoreServices
                                    .rescheduleAppointment,
                                appointmentId: appointmentId,
                                storeId: config.storeId,
                                storeNumber: storeNumber,
                            })
                        }
                    >
                        {getLabelFallback('MYAPPOINTMENTS_IN_STORE_SERVICES_RESCHEDULE_CTA_LABEL')}
                    </button>
                    <button
                        className="primary-button-negative button-size-small"
                        data-element-id="Account_BookAnAppointment"
                        data-description="Cancel"
                        onClick={() =>
                            goToExternalPage({
                                url: config.callFromComponentToTO.myAppointments.inStoreServices
                                    .cancelAppointment,
                                appointmentId: appointmentId,
                                storeId: config.storeId,
                                storeNumber: storeNumber,
                            })
                        }
                    >
                        {getLabelFallback(
                            'MYAPPOINTMENTS_IN_STORE_SERVICES_CANCEL_APPOINTMENT_CTA_LABEL'
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

//TODO: Mapping appType with the list of appointments types given from TAB
const renderWrapperBody = (
    appointment: Appointments,
    isMobile: any,
    physicalStoreInformations: AppointmentPhysicalStoreInformations
): JSX.Element => {
    const { visitPurpose } = appointment;
    const { phoneNumber, location } = physicalStoreInformations;
    return (
        <div
            role="region"
            aria-label="in-store services details visitpurpose"
            className="wrapper-summary-content"
        >
            {visitPurpose && (
                <>
                    <div className="wrapper-summary-content-label">
                        {getLabelFallback(
                            'MYAPPOINTMENTS_IN_STORE_SERVICES_APPOINTMENT_VISIT_PURPOS_LABEL'
                        )}
                    </div>
                    <div className="wrapper-summary-content-value">{visitPurpose}</div>
                </>
            )}

            <>
                <div className="wrapper-summary-content-label">
                    {getLabelFallback('MY_APPOINTMENTS_IN_STORE_SERVICES_LOCATION_LABEL')}
                </div>
                <div className="wrapper-summary-content-value">
                    {location && <div className="address">{getFullOfficeAddress(location)}</div>}
                    <div className="contacts">
                        {phoneNumber &&
                            (isMobile ? (
                                <a href={`tel:${phoneNumber}`} data-analytics_available_call="0">
                                    {phoneNumber}
                                </a>
                            ) : (
                                <div className="primary-message medium align-left">
                                    {phoneNumber}
                                </div>
                            ))}
                        {location && (
                            <a
                                href={`http://maps.google.com/?q=${getFullOfficeAddress(location)}`}
                                target="_blank"
                            >
                                {getLabelFallback(
                                    'MY_APPOINTMENTS_IN_STORE_SERVICES_DIRECTION_LABEL'
                                )}
                            </a>
                        )}
                    </div>
                </div>
            </>
        </div>
    );
};

const InStoreServicesAppointmentOverview = ({ appointment }: Props): JSX.Element => {
    const config = useSelector((state: RootState) => state.common.config);
    const isMobile = useSelector((state: RootState) => state.common.isMobile);
    const [physicalStoreInformations, setPhysicalStoreInformations] =
        useState<AppointmentPhysicalStoreInformations>();
    useEffect(() => {
        getPhysicalStoreInformations({
            isMockRequest: config.mockRequest,
            storeId: config.storeId,
            physicalStoreId: appointment.storeNumber,
        }).then(result => {
            const mappedPhysicalStoreInformations = mapPhysicalStoreInformations(
                result?.data?.PhysicalStore[0]
            );
            setPhysicalStoreInformations(mappedPhysicalStoreInformations);
        });

        return () => {
            setPhysicalStoreInformations(null);
        };
    }, [appointment]);

    return physicalStoreInformations ? (
        <WrapperSummary>
            {renderWrapperHeader(appointment, config)}
            {renderWrapperBody(appointment, isMobile, physicalStoreInformations)}
        </WrapperSummary>
    ) : (
        <LoaderTable />
    );
};

export default InStoreServicesAppointmentOverview;
